import React, {
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { Redirect, useParams } from 'react-router-dom';
import qs from 'qs';
import { routes, URL } from 'appConstants';
import {
  ButtonV4,
  HTMLContent,
  Link,
  Preloader,
} from 'components';
import { useRequest } from 'hooks';
import type {
  GetResponse,
  Resolution,
  State,
  PersonalResolutions,
} from 'types';
import { RequestStatus } from 'types';
import {
  _t,
  _t_back,
  dateToUiFormat,
  openApplicationForm,
  validateEmptyParams,
} from 'utils';

import banner from 'assets/img/main-v4/hero-curve.png';
import iconPlus from 'assets/img/main-v4/icon-plus.svg';
import iconArrow from 'assets/img/resolutions-v4/icon-arrow.svg';

import {
  getResolutionOrg,
  getResolutionRegion,
} from '../shared';
import styles from './styles.module.scss';

const RELATED_LIMIT = 3;

type Props = {
  crumbs?: Array<{ label: string, to: string }>,
};

const hasHtml = (value?: string) => {
  if (!value) {
    return false;
  }
  return value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().length > 0;
};

const ResolutionsDetails = ({ crumbs }: Props) => {
  const { id } = useParams<{ id: string }>();
  const isAuth = useSelector(({ me }: State) => me.isAuth);
  const token = useSelector(({ me }: State) => me.token);

  const [favButtonText, setFavButtonText] = useState('В избранное');
  const { data: favRes, request: getFavorite } = useRequest<Array<PersonalResolutions>>({
    errback: () => undefined,
  });
  const { data: favorite, request: setFavorite } = useRequest<{ created: string }>({
    errback: () => undefined,
  });
  const { data, status, request } = useRequest<Resolution>({
    errback: () => undefined,
  });
  const {
    data: relatedData,
    request: relatedRequest,
  } = useRequest<GetResponse<Resolution[]>>({
    errback: () => undefined,
  });

  useEffect(() => {
    request({
      method: 'get',
      url: `${URL.RESOLUTIONS.GET}${id}/`,
    });
    relatedRequest({
      method: 'get',
      url: URL.RESOLUTIONS.GET,
      params: validateEmptyParams({
        params: {
          limit: RELATED_LIMIT + 5,
          offset: 0,
        },
      }),
      paramsSerializer: (value) => qs.stringify(value, { indices: false }),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!token) {
      return;
    }
    getFavorite({
      method: 'GET',
      url: URL.PERSONAL.RESOLUTIONS.GET,
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => {
    favRes?.forEach((fav) => {
      if (fav.resolution.id === data?.id || favorite?.created) {
        setFavButtonText('В избранном');
      }
    });
  }, [favRes, data, favorite]);

  const title = _t_back(data, 'res_title') || _t('Мера поддержки');
  const region = data ? getResolutionRegion(data) : '';
  const org = data ? getResolutionOrg(data) : '';
  const deadline = data?.application_deadline
    ? dateToUiFormat(data.application_deadline)
    : '';
  const shortDescription = _t_back(data, 'short_description');
  const howToGet = _t_back(data, 'how_to_get_support');
  const documents = _t_back(data, 'document_list');
  const comments = _t_back(data, 'comments');
  const information = _t_back(data, 'information_about_res');

  const sections = useMemo(() => ([
    { id: 'main', title: '', content: shortDescription },
    { id: 'how', title: 'Как получить поддержку?', content: howToGet },
    { id: 'docs', title: 'Документы', content: documents },
    { id: 'info', title: 'Информация о мере', content: information },
    { id: 'comments', title: 'Комментарии', content: comments },
  ].filter((item) => hasHtml(item.content))), [
    shortDescription,
    howToGet,
    documents,
    information,
    comments,
  ]);

  const related = useMemo(() => {
    const items = (relatedData?.results || [])
      .filter((item) => (
        String(item.slug) !== String(id) &&
        item.id !== data?.id
      ))
      .slice(0, RELATED_LIMIT);

    return items.map((item) => ({
      id: item.id,
      slug: item.slug,
      region: getResolutionRegion(item),
      title: _t_back(item, 'res_title'),
      org: getResolutionOrg(item),
    }));
  }, [relatedData, id, data?.id]);

  const switchFavorite = () => {
    if (!token || !data?.id) {
      return;
    }
    setFavorite({
      method: 'post',
      url: URL.PERSONAL.RESOLUTIONS.SEND,
      data: {
        resolution: data.id,
      },
      headers: {
        Authorization: `Bearer ${token}`,
      },
    });
  };

  if (
    status === RequestStatus.ERROR ||
    (status === RequestStatus.SUCCESS && !data)
  ) {
    return <Redirect to={routes.resolutions.root} />;
  }

  return (
    <div className={styles.page}>
      <div className={styles.banner} aria-hidden>
        <img src={banner} alt="" />
      </div>

      <Preloader isLoading={status === RequestStatus.REQUEST || status === RequestStatus.INIT}>
        <section className={styles.head}>
          <h1 className={styles.title}>{title}</h1>
          <nav className={styles.crumbs} aria-label={_t('Навигация')}>
            <Link to={routes.main.root} className={styles.crumbLink}>{_t('Главная')}</Link>
            <span className={styles.crumbLine} aria-hidden />
            {(crumbs && crumbs.length ? crumbs : [
              { label: _t('Библиотека экспортера'), to: routes.resolutions.root },
            ]).map((crumb) => (
              <React.Fragment key={`${crumb.to}-${crumb.label}`}>
                <Link to={crumb.to} className={styles.crumbLink}>
                  {crumb.label}
                </Link>
                <span className={styles.crumbLine} aria-hidden />
              </React.Fragment>
            ))}
            <span className={styles.crumbCurrent}>{title}</span>
          </nav>
        </section>

        <div className={styles.layout}>
          <article className={styles.article}>
            <div className={styles.meta}>
              {region ? <span className={styles.regionBadge}>{region}</span> : null}
              {deadline ? <span className={styles.dateBadge}>{deadline}</span> : null}
            </div>

            <div className={styles.body}>
              {org ? <p className={styles.orgLine}>{org}</p> : null}

              {sections.map((section) => (
                <div key={section.id} className={styles.section}>
                  {section.title ? (
                    <h2 className={styles.sectionTitle}>{_t(section.title)}</h2>
                  ) : null}
                  <HTMLContent content={section.content} className={styles.html} />
                </div>
              ))}

              <div className={styles.actions}>
                <ButtonV4
                  type="button"
                  label="Оформить заявку"
                  variant="outline"
                  tone="onLight"
                  iconSrc={iconPlus}
                  onClick={() => openApplicationForm({
                    type: 'resolution',
                    id: data?.id || id,
                    title,
                    path: `${routes.resolutions.root}/${data?.slug || id}`,
                  })}
                />
                {isAuth ? (
                  <ButtonV4
                    type="button"
                    label={favButtonText}
                    variant="outline"
                    tone="onLight"
                    onClick={switchFavorite}
                  />
                ) : null}
              </div>
            </div>
          </article>

          <aside className={styles.aside}>
            <h2 className={styles.asideTitle}>{_t('Смотрите также')}</h2>
            <ul className={styles.asideList}>
              {related.map((item) => {
                const to = `${routes.resolutions.root}/${item.slug || item.id}`;
                return (
                  <li key={item.id} className={styles.asideCard}>
                    <span className={styles.asideBadge}>{item.region}</span>
                    <div className={styles.asideBody}>
                      <Link to={to} className={styles.asideCardTitle}>{item.title}</Link>
                      {item.org ? (
                        <p className={styles.asideMeta}>{item.org}</p>
                      ) : null}
                      <ButtonV4
                        to={to}
                        label="Подробнее"
                        variant="outline"
                        tone="onLight"
                        iconSrc={iconArrow}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </Preloader>
    </div>
  );
};

export default ResolutionsDetails;
